import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatCalendar } from '@angular/material/datepicker';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { EmeraldIconModule } from '@emerald-angular/icon';
import { EmeraldButtonModule } from '@emerald-angular/button';
import { TimeSheet } from '@pages/ops-vendor-management/ops-timesheet/ops-timesheet-config';
import { VendorService } from '@app/shared/services/vendor.service';
import { GlobalErrorService } from '@app/shared/services/global-error.service';
import { AuthService } from '@app/core/auth.service';
import { TimesheetCalendarHelper, MarkedDate } from '@pages/ops-vendor-management/ops-timesheet-calendar/ops-timesheet-calendar.helper';
import { LeaveTypeEnum } from '@constants/global.enum';
import { SummaryItem } from '@interfaces/vendor.model';
import { SUMMARY_KEYS } from '@constants/vendor-constants';

interface DetailRow {
  label: string;
  value: string | number | undefined;
}

export interface LastWorkingDayPayload {
  employeeId: string;
  lastWorkingDay: string | null;
  city: string;
  updatedBy: string;
}

@Component({
  selector: 'app-employee-details',
  standalone: true,
  imports: [
    CommonModule,
    MatCalendar,
    MatDatepickerModule,
    MatNativeDateModule,
    EmeraldIconModule,
    EmeraldButtonModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './employee-details.component.html',
  styleUrl: './employee-details.component.scss',
})
export class EmployeeDetailsComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() employee: TimeSheet | null = null;
  @Input() show: boolean = false;
  @Input() month: number = new Date().getMonth() + 1;
  @Input() year: number = new Date().getFullYear();
  @Output() closePopup = new EventEmitter<void>();
  @Output() lastWorkingDayUpdated = new EventEmitter<LastWorkingDayPayload>();

  @ViewChild('calendar') calendarRef!: MatCalendar<Date>;

  // Track which month/year is currently displayed in the calendar
  private activeCalendarMonth: number = 0;
  private activeCalendarYear: number = 0;
  private calendarStateSub: Subscription | null = null;

  generalDetails: DetailRow[] = [];
  timesheetDetails: DetailRow[] = [];
  lastWorkingDay: string = '';
  selectedDate: Date | null = null;
  isCalendarOpen = false;
  isLocationDropdownOpen = false;
  locations = ['Hyderabad', 'Gurugram', 'Noida'];

  // Restrict to today and earlier — no future dates
  maxDate: Date = new Date();

  // Leave / holiday highlighting
  apiMarkedDates: MarkedDate[] = [];
  summary: SummaryItem[] = [];
  isLoadingLeaves = false;
  dateClass = (_date: Date): string => '';

  private timesheetHelper = new TimesheetCalendarHelper();

  constructor(
    private vendorService: VendorService,
    private errorHandler: GlobalErrorService,
    private authService: AuthService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['employee'] && this.employee) {
      this.buildDetails();
    }
    if (changes['show'] && !this.show) {
      this.isCalendarOpen = false;
    }
  }

  ngAfterViewInit(): void {
    // Subscription wired after calendar renders — see subscribeToCalendarNavigation()
  }

  ngOnDestroy(): void {
    this.calendarStateSub?.unsubscribe();
  }

  private subscribeToCalendarNavigation(): void {
    // Unsubscribe any previous subscription first
    this.calendarStateSub?.unsubscribe();
    if (!this.calendarRef) return;

    this.activeCalendarMonth = this.calendarRef.activeDate.getMonth() + 1;
    this.activeCalendarYear  = this.calendarRef.activeDate.getFullYear();

    this.calendarStateSub = this.calendarRef.stateChanges.subscribe(() => {
      const newMonth = this.calendarRef.activeDate.getMonth() + 1;
      const newYear  = this.calendarRef.activeDate.getFullYear();

      // Only re-fetch when the displayed month actually changes
      if (newMonth !== this.activeCalendarMonth || newYear !== this.activeCalendarYear) {
        this.activeCalendarMonth = newMonth;
        this.activeCalendarYear  = newYear;
        this.fetchLeaveDataForMonth(newMonth, newYear);
      }
    });
  }

  private buildDetails(): void {
    const e = this.employee!;

    this.generalDetails = [
      { label: 'Employee ID',       value: e.employeeId },
      { label: 'Name',              value: e.employeeName },
      { label: 'Job Title',         value: e.jobTitle },
      { label: 'Employee Status',   value: e.employeeStatus },
      { label: 'Vendor',            value: e.vendorName },
      { label: 'D&T Leader',        value: e.l3ManagerName },
      { label: 'Reporting Manager', value: e.reportingManager },
      { label: 'Project',           value: e.projectName },
    ].filter(row => row.value !== undefined && row.value !== null && row.value !== '');

    this.timesheetDetails = [
      { label: 'Working Hours',  value: e.workingHrs  !== undefined ? `${e.workingHrs} Hrs`  : undefined },
      { label: 'Billable Hours', value: e.billableHrs !== undefined ? `${e.billableHrs} Hrs` : undefined },
      { label: 'Leaves Availed', value: e.leavesAvailed !== undefined ? `${e.leavesAvailed}` : undefined },
      { label: 'Status',         value: e.status },
    ].filter(row => row.value !== undefined && row.value !== null && row.value !== '');

    this.lastWorkingDay = e.lastWorkingDay ? this.formatDate(e.lastWorkingDay) : '';
    this.selectedDate   = e.lastWorkingDay ? new Date(e.lastWorkingDay) : null;
    this.isCalendarOpen = false;
    this.isLocationDropdownOpen = false;
    this.apiMarkedDates = [];
    this.summary = [];
    this.dateClass = (_date: Date): string => '';
  }

  toggleCalendar(): void {
    this.isCalendarOpen = !this.isCalendarOpen;
    if (this.isCalendarOpen) {
      // fetchLeaveData for initial month, then subscribe to navigation after render
      this.fetchLeaveData();
      setTimeout(() => this.subscribeToCalendarNavigation());
    } else {
      this.calendarStateSub?.unsubscribe();
      this.calendarStateSub = null;
    }
  }

  toggleLocationDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
  }

  onLocationSelected(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const location = selectElement.value;
    if (this.employee) {
      this.employee.location = location;
      this.updateLastWorkingDay(
        this.lastWorkingDay
          ? this.toISODateString(new Date(this.lastWorkingDay))
          : null,
        location,
      );
    }
    this.isLocationDropdownOpen = false;
  }

  deleteLastWorkingDay(): void {
    this.lastWorkingDay = '';
    this.selectedDate = null;
    this.updateLastWorkingDay(null, this.employee?.location || '');
  }

  private fetchLeaveData(): void {
    this.fetchLeaveDataForMonth(this.month, this.year);
  }

  private fetchLeaveDataForMonth(month: number, year: number): void {
    if (!this.employee?.employeeId || this.isLoadingLeaves) return;

    this.isLoadingLeaves = true;
    this.vendorService
      .getLeaveSummary(
        this.employee.employeeId,
        this.employee.location || '',
        (this.employee as any).vendorId || null,
        month,
        year,
      )
      .subscribe({
        next: (data: any) => {
          this.apiMarkedDates = this.timesheetHelper.processLeaveDates(data);
          this.summary = this.timesheetHelper.processLeaveSummary(data, SUMMARY_KEYS);
          this.dateClass = this.buildDateClass();
          this.timesheetHelper.refreshCalendar(this.apiMarkedDates, LeaveTypeEnum);
          if (this.calendarRef) this.calendarRef.updateTodaysDate();
          this.isLoadingLeaves = false;
        },
        error: (error: any) => {
          this.errorHandler.logComponentError(error);
          this.isLoadingLeaves = false;
        },
      });
  }

  getFilteredSummary(): SummaryItem[] {
    return this.timesheetHelper.getFilteredSummary(this.summary, SUMMARY_KEYS);
  }

  private buildDateClass(): (date: Date) => string {
    const marked = this.apiMarkedDates;
    return (date: Date): string => {
      const dateStr = this.toISODateString(date);
      const found = marked.find(md => md.date === dateStr);
      if (!found) return '';
      switch (found.type) {
        case LeaveTypeEnum.Paid:       return LeaveTypeEnum.Paid;
        case LeaveTypeEnum.Unpaid:     return LeaveTypeEnum.Unpaid;
        case LeaveTypeEnum.HalfPaid:   return LeaveTypeEnum.HalfPaid;
        case LeaveTypeEnum.HalfUnpaid: return LeaveTypeEnum.HalfUnpaid;
        case LeaveTypeEnum.Public:     return LeaveTypeEnum.PublicHoliday;
        default:                       return '';
      }
    };
  }

  onDateSelected(date: Date | null): void {
    if (!date) return;
    this.selectedDate = date;
    const iso = this.toISODateString(date);
    this.lastWorkingDay = this.formatDate(iso);
    this.isCalendarOpen = false;

    this.updateLastWorkingDay(iso, this.employee?.location || '');
  }

  private updateLastWorkingDay(lwd: string | null, location: string): void {
    if (!this.employee) return;

    const updatedBy = this.authService.userProfile?.email || '';
    const payload = {
      employeeId: this.employee.employeeId,
      lastWorkingDay: lwd,
      city: location,
      updatedBy,
    };

    this.vendorService.updateLastWorkingDay(payload).subscribe({
      next: () => {
        this.lastWorkingDayUpdated.emit({
          employeeId: payload.employeeId,
          lastWorkingDay: payload.lastWorkingDay,
          city: payload.city,
          updatedBy: payload.updatedBy,
        });
      },
      error: (err) => this.errorHandler.logComponentError(err),
    });
  }

  onClose(): void {
    this.isCalendarOpen = false;
    this.closePopup.emit();
  }

  onBackdropClick(): void {
    this.onClose();
  }

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  private toISODateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}